/**
 * Core Axios HTTP Client and Shared Helpers
 */
import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance with default configuration
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to convert date from YYYY-MM-DD or Date to ISO 8601 DateTime
export const convertToISODateTime = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) {
    const year = dateInput.getFullYear();
    const month = String(dateInput.getMonth() + 1).padStart(2, '0');
    const day = String(dateInput.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
  }
  const dateString = String(dateInput);
  if (dateString.includes('T')) return dateString;
  return `${dateString}T00:00:00.000Z`;
};


export const normalizeRoleName = (roleName) => (roleName ? roleName.trim().toLowerCase() : '');

/**
 * Common error handler for API responses
 */
export const handleApiError = (error, defaultMessage = 'Errore di connessione al server') => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  const isIncompatibleError = typeof message === 'string' && message.includes('INCOMPATIBLE_SUBSCRIPTIONS:');
  return {
    success: false,
    message,
    ...(isIncompatibleError ? { isIncompatibleSubscriptions: true } : {}),
  };
};

/**
 * Helper function to fetch all data for export (bypassing pagination)
 */
export const fetchAllDataRest = async (url, params = {}, serviceName = 'dati') => {
  try {
    // 1. Get total count
    const countResponse = await api.get(url, { params: { ...params, page: 1, limit: 1 } });
    const totalCount = countResponse.data?.totalCount ?? (countResponse.data?.length || 0);

    if (totalCount === 0) {
      return {
        success: true,
        data: { items: [], totalCount: 0 },
      };
    }

    // 2. Fetch all with total count limit
    const allResponse = await api.get(url, {
      params: { ...params, page: 1, limit: totalCount },
      timeout: 120000,
    });

    return {
      success: true,
      data: allResponse.data,
    };
  } catch (error) {
    console.error(`Export ${serviceName} error:`, error);
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        success: false,
        message: `Timeout durante l'esportazione ${serviceName}. Applicare filtri più restrittivi.`,
      };
    }
    return handleApiError(error, `Errore durante l'esportazione ${serviceName}. Applicare filtri più restrittivi.`);
  }
};

export const fetchAllData = fetchAllDataRest;

// Request interceptor to add auth token
// sessionStorage has priority over localStorage: the impersonated tab stores its token
// in sessionStorage so it doesn't interfere with the admin session in other tabs.
api.interceptors.request.use(
  (config) => {
    const token =
      sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and redirect on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.config?.url !== '/api/log') {
      import('../logService')
        .then(({ logService }) => {
          const errorMessage = error.response?.data?.message || error.message || 'API Error';
          logService.logError(`API Error: ${errorMessage}`, error, {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
          });
        })
        .catch(() => {});
    }

    if (error.response?.status === 401) {
      if (sessionStorage.getItem('authToken')) {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
        try {
          window.close();
        } catch (_) {}
        window.location.href = '/login';
      } else {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
